/* eslint-disable prettier/prettier */
import { useEffect, useRef, useState } from "react"

import {
  useForm,
  usePayloadFormatters,
  useStepTransition
} from "@garantidos/hooks"
import { proposalHttpController } from "@garantidos/services"
import { focusOnLogo } from "@garantidos/utils"

import scrollTo from "@mobi/utils/browser/scrollTo"

import { financed_cgi_app_link } from "constants"
import validatorsDictionary from "pages/Proposal/hooks/validators"

import { ProposalContext, useProposalContext } from "contexts/proposal"

import { alertMessages } from "./data"
import { fields } from "./fields"
import * as trackers from "./trackings"

const useQualification = () => {
  const { form } = useForm({
    fields,
    context: ProposalContext,
    validatorsDictionary
  })

  const {
    stepInfo,
    cpfData,
    fetchProposalData,
    sendProposalData,
    proposalData,
    errorMessage,
    setErrorMessage,
    isLoading,
    errorHandler,
    setIsStepVisible
  } = useProposalContext()

  const { stepNavigator } = useStepTransition(setIsStepVisible)
  const {
    qualification_property_status,
    qualification_property_owner,
    qualification_property_registration_updated
  } = proposalData

  const alertRef = useRef(null)
  const settledRef = useRef(null)
  const propertyOwnerRef = useRef(null)
  const PropertyRegistrationUpdatedRef = useRef(null)

  const [isValidForm, setIsValidForm] = useState(false)

  const [isSettled, setIsSettled] = useState(null)
  const [isFinanced, setIsFinanced] = useState(null)
  const [isPropertyOwner, setIsPropertyOwner] = useState(null)
  const [isPropertyRegistrationUpdated, setIsPropertyRegistrationUpdated] =
    useState(null)

  const [isUnqualified, setIsUnqualified] = useState(false)
  const [unqualifiedMessage, setUnqualifiedMessage] = useState("")

  const { formatSendPayload } = usePayloadFormatters(fields)
  const { previousStep, nextStep } = stepInfo
  const { hashedCpf } = cpfData

  const handleTracking = (labelTrackEvent) => {
    trackers.event(labelTrackEvent, hashedCpf)
  }

  const handleUnqualifiedTracking = () => {
    trackers.unqualified(
      hashedCpf,
      isSettled,
      isPropertyOwner,
      isPropertyRegistrationUpdated
    )
  }

  const handleUnqualified = (type, callback = null) => {
    setIsUnqualified(true)
    setUnqualifiedMessage(alertMessages[type])
    callback && callback(false)
  }

  const lastQualificationAnswersSended = useRef({})

  useEffect(() => {
    function checkIfQualificaitonAnswerIsChanged(field, currValue) {
      return (
        currValue != undefined &&
        lastQualificationAnswersSended.current[field] !== currValue
      )
    }

    async function sendQualificationAnswers() {
      if (
        !lastQualificationAnswersSended.current ||
        checkIfQualificaitonAnswerIsChanged(
          "qualification_property_status",
          qualification_property_status
        ) ||
        checkIfQualificaitonAnswerIsChanged(
          "qualification_property_owner",
          qualification_property_owner
        ) ||
        checkIfQualificaitonAnswerIsChanged(
          "qualification_property_registration_updated",
          qualification_property_registration_updated
        )
      ) {
        const payload = {
          qualification_property_status,
          qualification_property_owner,
          qualification_property_registration_updated
        }

        lastQualificationAnswersSended.current = payload
        await proposalHttpController({
          payload,
          method: "sendQualificationAnswers"
        })
      }
    }
    sendQualificationAnswers()
  }, [
    qualification_property_status,
    qualification_property_owner,
    qualification_property_registration_updated
  ])

  useEffect(() => {
    if (qualification_property_status === "quitado") {
      setIsFinanced(false)
      setIsSettled(true)
      setIsUnqualified(false)
      setUnqualifiedMessage("")
      if (qualification_property_owner) {
        setIsPropertyOwner(true)
        if (qualification_property_registration_updated) {
          setIsPropertyRegistrationUpdated(true)
          setIsValidForm(true)
        }
      }
    }

    qualification_property_registration_updated === false &&
      handleUnqualified(
        "outdatedRegistration",
        setIsPropertyRegistrationUpdated
      )

    qualification_property_owner === false &&
      handleUnqualified("notPropertyOwner", setIsPropertyOwner)

    if (qualification_property_status === "financiado") {
      handleUnqualified("", setIsSettled)
      setIsFinanced(true)
    }

    if (qualification_property_status === "nao_tenho_imovel") {
      setIsFinanced(false)
      handleUnqualified("withoutProperty", setIsSettled)
    }
  }, [
    qualification_property_status,
    qualification_property_owner,
    qualification_property_registration_updated
  ])

  useEffect(() => {
    isSettled && scrollTo(propertyOwnerRef.current)
  }, [isSettled])

  useEffect(() => {
    if (isPropertyOwner === null) return
    const isPropertyOwnerLabel = isPropertyOwner ? "Sim" : "Não"
    handleTracking(`Dono${isPropertyOwnerLabel}`)
    trackers.ownership(isPropertyOwnerLabel.toLowerCase(), hashedCpf)

    isPropertyOwner && scrollTo(PropertyRegistrationUpdatedRef.current)
  }, [isPropertyOwner])

  useEffect(() => {
    if (isUnqualified) {
      scrollTo(alertRef.current)
      setIsValidForm(false)
    }
  }, [isUnqualified])

  useEffect(() => {
    if (isPropertyRegistrationUpdated === null) return
    const isPropertyRegistrationUpdatedLabel = isPropertyRegistrationUpdated
      ? "Sim"
      : "Não"
    handleTracking(`Cartorio${isPropertyRegistrationUpdatedLabel}`)
    trackers.registration(
      isPropertyRegistrationUpdatedLabel.toLowerCase(),
      hashedCpf
    )
  }, [isPropertyRegistrationUpdated])

  useEffect(() => {
    qualification_property_status === "quitado" &&
      (handleTracking("CliqueQuitado"), trackers.status("quitado", hashedCpf))

    qualification_property_status === "financiado" &&
      (handleTracking("CliqueFinanciado"),
      trackers.status("financiado", hashedCpf))

    qualification_property_status === "nao_tenho_imovel" &&
      (handleTracking("CliqueNaoTenhoImovel"),
      trackers.status("nao-tenho", hashedCpf))
  }, [qualification_property_status])

  const trackDsBackButton = () => {
    trackers.backHome(hashedCpf)
  }

  useEffect(() => {
    trackers.pageview(hashedCpf)

    focusOnLogo()

    const back = document.getElementById("main-logo")
    back.onclick = () => trackDsBackButton()
  }, [])

  const onSubmitCallback = async (formData) => {
    if (isFinanced) {
      window.open(financed_cgi_app_link, "_self")
      return
    }

    try {
      const payload = formatSendPayload(formData)
      await sendProposalData({ payload })
      trackers.advanceGA4(hashedCpf)
      stepNavigator("next", nextStep)
    } catch (error) {
      trackers.alert(hashedCpf, error)
      errorHandler(error)
    }
  }

  const handlePrevStep = async () => {
    if (isLoading) return
    try {
      await fetchProposalData({
        step: previousStep,
        mock: true,
        recaptcha: true
      })
      trackers.back(hashedCpf)
      stepNavigator("prev", previousStep)
    } catch (error) {
      trackers.alert(hashedCpf, error)
      errorHandler(error)
    }
  }

  return {
    handlePrevStep,
    onSubmitCallback,
    fields,
    form,
    errorMessage,
    setErrorMessage,
    isSettled,
    isUnqualified,
    isPropertyOwner,
    unqualifiedMessage,
    settledRef,
    propertyOwnerRef,
    PropertyRegistrationUpdatedRef,
    alertRef,
    isValidForm,
    handleUnqualifiedTracking,
    isLoading,
    isFinanced
  }
}

export default useQualification
